import { CommonModule } from "@angular/common";
import { Component, computed, inject, input } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { SecuredImageComponent } from "@shared/components/secured-image.component";
import { EnvConfigService } from "app/env-config.service";
import { FileDTO } from "app/modules/adaptive-ui-flow/file-dto";

@Component({
  selector: "app-file-preview",
  standalone: true,
  imports: [CommonModule, MatIconModule, SecuredImageComponent],
  templateUrl: "./file-preview.component.html",
})
export class FilePreviewComponent {
  // File DTO received for already-saved files. This contains file data so we can fetch and preview the file.
  file = input<FileDTO | null>(null);
  /**
   * Image URL for newly uploaded or captured files.
   * This is typically a Base64 string when the file is not yet saved on the server.
   */
  imageUrl = input<string | null>(null);

  customClass = input<string | null>(null);

  fileType = input<String | null>(null);

  private sanitizer = inject(DomSanitizer);
  private envConfig = inject(EnvConfigService);

  /** Final URL to preview (auto recalculated when inputs change) */
  finalUrl = computed(() => {
    // If new image uploaded or captured
    if (this.imageUrl()) {
      return this.normalizeImageUrl(this.imageUrl());
    }

    // If file exists but no imageUrl yet
    if (this.file()?.id) {
      return `/api/files/stream/${this.file().id}`;
    }

    return null;
  });

  normalizeImageUrl(inputUrl: any): any {
    if (!inputUrl) return inputUrl;

    // Case 1: Already a plain image URL (e.g., base64)
    if (typeof inputUrl === "string") return inputUrl;

    // Case 2: Angular SafeUrl containing a base64 image → unwrap safely
    const rawUrl = inputUrl.changingThisBreaksApplicationSecurity;
    if (rawUrl && rawUrl.startsWith("data:image")) {
      return rawUrl;
    }

    // Case 3: PDFs or blob URLs → keep SafeUrl object (do NOT unwrap)
    return inputUrl;
  }

  isBlobURL(urlPassed: any) {
    const raw = urlPassed?.changingThisBreaksApplicationSecurity;
    return raw?.startsWith("blob") ?? false;
  }

  getValidPDFURL(urlPassed: any) {
    if (urlPassed["changingThisBreaksApplicationSecurity"]) {
      const url = urlPassed["changingThisBreaksApplicationSecurity"];
      if (url.startsWith("blob")) {
        return urlPassed;
      }
      return this.sanitizer.bypassSecurityTrustResourceUrl(`${this.envConfig.envConfig.APIBASEURL}/${url}`);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(`${this.envConfig.envConfig.APIBASEURL}${urlPassed}`);
  }

  getClass() {
    return this.customClass() || "w-40 h-40";
  }

  type = computed(() => {
    const type = this.fileType()?.toLowerCase() || '';
    return {
      isPdf: type === 'application/pdf',
      isJson: type === 'application/json',
      isImage: type.startsWith('image/') || type.startsWith('data:image/')
    };
  });
}
